import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getToken, getUser, clearSession } from "../api/session";
import { getCurrentUser } from "../api/auth";

// Gate for /admin. Renders nothing until the stored token has been checked
// against the API, so a stale localStorage entry cannot reveal the panel.
function ProtectedRoute({ children, role = "admin" }) {
    const location = useLocation();

    const [status, setStatus] = useState(() => (getToken() ? "checking" : "denied"));

    useEffect(() => {
        let cancelled = false;

        const verify = async () => {
            if (!getToken()) {
                setStatus("denied");
                return;
            }

            try {
                const data = await getCurrentUser();

                if (cancelled) return;

                if (role && data.user?.role !== role) {
                    setStatus("denied");
                    return;
                }

                setStatus("allowed");
            } catch (error) {
                if (cancelled) return;

                clearSession();
                setStatus("denied");
            }
        };

        verify();

        return () => {
            cancelled = true;
        };
    }, [role, location.pathname]);

    if (status === "denied") {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (status === "checking") {
        // Optimistic render for a session that already looks right, so navigating
        // between admin pages does not flash a loader on every click.
        if (getUser()?.role === role) return children;

        return (
            <div className="grid min-h-[60vh] place-items-center bg-ivory">
                <p className="font-display text-2xl text-muted">Checking your session…</p>
            </div>
        );
    }

    return children;
}

export default ProtectedRoute;
